'use client'

import { useSession } from 'next-auth/react'
import { notFound, useRouter } from 'next/navigation'
import { ReactNode, Suspense, useEffect } from 'react'

import HelpWidget from '@components/app/help-widget'
import Spinner from '@components/ui/spinner'
import { useAppRoute } from '@lib/use-app-route'
import { getErrorCode } from '@lib/trpc'

import { MobileActionProvider } from './mobile-action'
import MobileNav from './sidebar/mobile-nav'
import Sidebar from './sidebar'

const useLoginRedirect = () => {
  const router = useRouter()
  const { status } = useSession()

  useEffect(() => {
    if (status !== 'unauthenticated') return
    const { pathname, search } = window.location
    router.replace(`/login?redirect=${encodeURIComponent(`${pathname}${search}`)}`)
  }, [status, router])

  return status
}

const LoginRedirect = () => {
  useLoginRedirect()
  return null
}

export const EnsureLoggedIn = ({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) => {
  const status = useLoginRedirect()

  if (status !== 'authenticated') {
    if (fallback) return <>{fallback}</>
    return (
      <div className="flex min-h-dvh items-center justify-center bg-o-sidebar">
        <Spinner />
      </div>
    )
  }

  return <>{children}</>
}

const isInaccessibleTeam = (error: unknown) => {
  const code = getErrorCode(error)
  return code === 'NOT_FOUND' || code === 'FORBIDDEN'
}

const EnsureTeamAccess = () => {
  const { teamSlug, teamError, envInvalid } = useAppRoute()

  if (!teamSlug || envInvalid || isInaccessibleTeam(teamError)) notFound()

  return null
}

const PageSpinner = () => (
  <div className="flex flex-1 items-center justify-center">
    <Spinner />
  </div>
)

const SidebarPlaceholder = () => <div aria-hidden className="sticky top-0 hidden h-dvh w-64 shrink-0 md:block" />

const MobileNavPlaceholder = () => <div aria-hidden className="h-[60px] shrink-0 bg-o-panel md:hidden" />

const AppLayout = ({ children }: { children: ReactNode }) => (
  <>
    <LoginRedirect />
    <Suspense fallback={null}>
      <EnsureTeamAccess />
    </Suspense>
    <MobileActionProvider>
    <div className="flex min-h-dvh flex-col bg-o-panel font-sans leading-tight md:h-dvh md:flex-row md:bg-o-sidebar">
      <Suspense fallback={<SidebarPlaceholder />}>
        <Sidebar />
      </Suspense>
      <Suspense fallback={<MobileNavPlaceholder />}>
        <MobileNav />
      </Suspense>
      <main className="@container/page flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-o-panel px-4 pb-8 pt-3.5 md:rounded-s-2xl md:border-s md:border-o-panel-edge md:px-12 md:py-9">
        <Suspense fallback={<PageSpinner />}>{children}</Suspense>
      </main>
    </div>
    </MobileActionProvider>
    <Suspense fallback={null}>
      <HelpWidget />
    </Suspense>
  </>
)

export default AppLayout
